import {
    Accordion,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { AddFill } from '@archipro-design/icons';
import { AccordionContent, AccordionTitle } from '@fluentui/react-northstar';
import type { AccordionSchema } from '../../type';
import * as S from './FaqSection.styles';
import React from 'react';
import CollapsibleContainer from '~/modules/product/component/product-details/CollapsibleContainer';

interface FAQSectionProps {
    faqSchemaPanels: AccordionSchema[];
    footerContent?: string;
}

export const FAQSection = (props: FAQSectionProps) => {
    const { faqSchemaPanels, footerContent } = props;
    const { css } = useStyles();
    const { desktop } = useAppDisplayModeContext();
    const [expandedPanels, setExpandedPanels] = React.useState<number[]>([0]);

    const filteredPanels = React.useMemo(
        () => faqSchemaPanels.filter((faq) => faq.content && faq.title),
        [faqSchemaPanels]
    );

    React.useEffect(() => {
        if (filteredPanels.length) {
            setExpandedPanels([0]);
        }
    }, [filteredPanels]);

    if (filteredPanels.length === 0 && !footerContent) {
        return null;
    }

    return (
        <div className={css(S.FaqSectionWrapper)}>
            {footerContent && (
                <Accordion
                    expanded={true}
                    renderPanelTitle={(Component, props) => (
                        <Component indicator={null} {...props} />
                    )}
                    panels={[
                        {
                            title: {
                                key: 'static-footer-title-panel',
                                id: 'static-footer-title-panel',
                                accordionContentId:
                                    'static-footer-content-panel',
                            },
                            content: {
                                key: 'static-footer-content-panel',
                                id: 'static-footer-content-panel',
                                accordionTitleId: 'static-footer-title-panel',
                                content: (
                                    <div
                                        className={css(S.FaqHTMLContent)}
                                        dangerouslySetInnerHTML={{
                                            __html: footerContent,
                                        }}
                                    />
                                ),
                            },
                        },
                    ]}
                />
            )}
            <Accordion>
                {filteredPanels.map((faq, i) => {
                    const isActive = expandedPanels.includes(i);
                    return (
                        <React.Fragment key={'faq-panel-' + i}>
                            <AccordionTitle
                                className={css(S.AccordionTitle)}
                                index={i}
                                onClick={() => {
                                    setExpandedPanels(
                                        isActive
                                            ? expandedPanels.filter(
                                                  (ii) => ii !== i
                                              )
                                            : [...expandedPanels, i]
                                    );
                                }}
                                content={{
                                    children: (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: faq.title || '',
                                            }}
                                        />
                                    ),
                                }}
                                indicator={
                                    <AddFill
                                        className={
                                            isActive
                                                ? css(
                                                      S.IndicatorIcon,
                                                      S.IndicatorIconActive
                                                  )
                                                : css(S.IndicatorIcon)
                                        }
                                        variables={{
                                            '1xSize': desktop
                                                ? pxToRem(32)
                                                : pxToRem(17.5),
                                        }}
                                    />
                                }
                            />
                            <CollapsibleContainer visible={isActive}>
                                <AccordionContent active={true}>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: faq.content || '',
                                        }}
                                    />
                                </AccordionContent>
                            </CollapsibleContainer>
                        </React.Fragment>
                    );
                })}
            </Accordion>
        </div>
    );
};
